import React, { useState } from 'react';
import PaymentFormFour from './PaymentFormFour';
import '../index.css';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import stateTaxRates from './StateTaxRates.json';
import { FaSpinner } from 'react-icons/fa';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const passTypeMapping = {
  hop_pass: 'Hop Pass',
  hop_deal: 'Hop Deal',
  event_hop: 'Event Hop',
  season_pass: 'Season Pass',
  cover_charge: 'Cover Charge',
  other: 'Other',
};

const PayTab = ({ bar, passes }) => {
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedPassType, setSelectedPassType] = useState(null);
  const [payFormLoading, setPayFormLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const stateAbbr = bar?.address?.state ?? '';
  const DEFAULT_TAX_RATE = 0.0825; // Default to 8.25% tax
  const TAX_RATE = stateTaxRates[stateAbbr.toUpperCase()] || DEFAULT_TAX_RATE;

  const passTypeOptions = [
    { label: 'All Passes', value: null },
    ...Object.entries(passTypeMapping).map(([key, label]) => ({ label, value: key })),
  ];

  const openPaymentModal = async (pass) => {
    setPayFormLoading(true);
    const quantity = quantities[pass.offering_id] || 1;
    const totalAmount = Math.round(pass.price * quantity * (1 + TAX_RATE)); // Include tax in the total amount
  
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_AWS_URL}/${process.env.REACT_APP_STAGE}/createPaymentIntent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price: totalAmount,
          fein: bar.fein,
          bar_id: bar.bar_id,
          bar_name: bar.venueName,
          pass: pass,
          passDate: selectedDate,
        }),
      });
  
      const data = await response.json();
  
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setPaymentIntentId(data.paymentIntentId);
        setSelectedPass(pass);
        setIsPaymentModalVisible(true);
        setPayFormLoading(false);
      } else {
        throw new Error("Invalid response from server.");
      }
    } catch (err) {
      console.error('Failed to fetch payment intent:', err);
      window.alert("Payment Error: Failed to fetch payment details. Please try again.");
      setPayFormLoading(false);
    }
  };  

  const closePaymentModal = () => {
    setIsPaymentModalVisible(false);
    setSelectedPass(null);
  };

  const incrementQuantity = (offering_id) => {
    setQuantities((prev) => ({
      ...prev,
      [offering_id]: (prev[offering_id] || 1) + 1,
    }));
  };

  const decrementQuantity = (offering_id) => {
    setQuantities((prev) => ({
      ...prev,
      [offering_id]: Math.max((prev[offering_id] || 1) - 1, 1),
    }));
  };

  // Helper: compare dates (ignoring time)
  const isSameDay = (date1, date2) =>
    date1.toISOString().split('T')[0] === date2.toISOString().split('T')[0];

  // Filter passes based on selected date and pass type
  const filteredPasses = passes.filter((pass) => {
    const passStart = pass.validFrom ? new Date(pass.validFrom) : null;
    const passEnd = pass.validTo ? new Date(pass.validTo) : null;

    const selectedDay = selectedDate.toLocaleDateString('en-US', { weekday: 'long' });

    const dateValid =
      (pass.validDays?.length > 0 ? pass.validDays.includes(selectedDay) : true) &&
      (passStart && passEnd ? isSameDay(selectedDate, passStart) || (selectedDate >= passStart && selectedDate <= passEnd) : true);

    return dateValid && (selectedPassType ? pass.passType === selectedPassType : true);
  });

  return (
    <div className="pay-tab">
      <p className="fine-print">
        FomoFrog is not responsible for adherence to dress code, age restrictions, open or closing time, personal conduct, or any other venue-based restrictions that may prohibit access to the fun. Tickets not honored due to venue-based restrictions are non-refundable. Act kindly, show respect, and have fun responsibly!
      </p>
      <div className="filter-container">
        <div className='date-picker-container'>
          <label>Select Date:</label>
          <input
            type="date"
            value={selectedDate.toISOString().split('T')[0]}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
          />
        </div>
        <label>Filter by Pass Type:</label>
        <select 
          value={selectedPassType || ''} 
          className='pass-type-picker' 
          onChange={(e) => setSelectedPassType(e.target.value === '' ? null : e.target.value)}
        >
          {passTypeOptions.map((option) => (
            <option key={option.value} value={option.value || ''}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {filteredPasses.length === 0 ? (
        <p className="modal-title">No Hops Available</p>
      ) : (
        filteredPasses.map((pass) => {
          // Calculate remaining passes (if maxQuantity is set)
          const remaining = pass.maxQuantity && pass.soldQuantity !== undefined 
            ? pass.maxQuantity - pass.soldQuantity 
            : null;
          return (
            <div key={pass.offering_id} className="hop-pass-container glassmorphic">
              <h3 className="hop-pass-title">{pass.title}</h3>
              <p className="hop-pass-description">
                {pass.passDescription || 'Custom Pass'}
              </p>
              {/* Urgency Message */}
              {remaining !== null && remaining <= 10 && remaining > 0 && (
                <p className="urgency-text">Hurry! Only {remaining} left!</p>
              )}
              <div className="pay-area">
                <div className="ticket-quantity-container">
                  <button onClick={() => decrementQuantity(pass.offering_id)} className="quantity-button">-</button>
                  <span className="quantity-display">{quantities[pass.offering_id] || 1}</span>
                  <button onClick={() => incrementQuantity(pass.offering_id)} className="quantity-button">+</button>
                </div>
                {/* Conditionally render the payment button */}
                {payFormLoading ? (
                  <div className="spinner-wrapper">
                    <FaSpinner className="spinner" />
                  </div>
                ) : (
                  <>
                    {pass.maxQuantity && pass.soldQuantity !== undefined && pass.soldQuantity >= pass.maxQuantity ? (
                      <button className="pay-button" disabled>
                        Sold Out!
                      </button>
                    ) : (
                      <button onClick={() => openPaymentModal(pass)} className="pay-button">
                        {pass.price > 0 
                          ? `Pay $${((pass.price * (quantities[pass.offering_id] || 1)) / 100).toFixed(2)}`
                          : 'Coming Soon'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })
      )}
      {isPaymentModalVisible && selectedPass && clientSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <PaymentFormFour
            isVisible={isPaymentModalVisible}
            onClose={closePaymentModal}
            bar={bar}
            amount={selectedPass.price * (quantities[selectedPass.offering_id] || 1)}
            quantity={quantities[selectedPass.offering_id] || 1}
            pass={selectedPass}
            passDate={selectedDate}
            clientSecret={clientSecret}
            paymentIntentId={paymentIntentId}
          />
        </Elements>
      )}
    </div>
  );
};

export default PayTab;